import { useMemo } from 'react';
import { useCallerInfo } from '../../hooks/use-caller-info';
import { useSoftphoneCallState } from '../../providers/softphone-call-state-provider';
import { useSoftphoneDirectory } from '../../providers/softphone-directory-provider';
import { useSoftphoneRouter } from '../../providers/softphone-router-provider';
import { useSoftphoneWidgetControl } from '../../providers/widget-control-provider';
import { IncomingCall } from '../../types';
import { useShowIncomingCallBar } from '../incoming-call-bar/incoming-cb-hook';
import { DesktopCallBars } from './desktop-call-bars';
import { MobileCallBars } from './mobile-call-bars';

type CallBarsProps = {
  hasActiveCalls: boolean;
  showIncomingCallsUI: boolean;
  incomingCalls: IncomingCall[];
  isMobile: boolean;
};

export const CallBars = ({ hasActiveCalls, showIncomingCallsUI, incomingCalls, isMobile }: CallBarsProps) => {
  const useGetPersonImageQuery = useSoftphoneDirectory((ctx) => ctx?.queries?.useGetPersonImageQuery);
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx?.mergedCallGroup);
  const open = useSoftphoneWidgetControl((ctx) => ctx.open);
  const close = useSoftphoneWidgetControl((ctx) => ctx.close);
  const mode = useSoftphoneRouter((ctx) => ctx.mode);
  const goToDefaultRoute = useSoftphoneRouter((ctx) => ctx.goToDefaultRoute);
  const isOpen = useSoftphoneWidgetControl((ctx) => ctx.isOpen);

  const incomingCallData = useShowIncomingCallBar({
    incomingCalls,
  });

  const callInfo = useCallerInfo({
    uri: incomingCallData.latestIncomingCall?.remoteParty.uri ?? '',
    displayName: incomingCallData.latestIncomingCall?.remoteParty.displayName,
    enabled: !mergedCallGroup && !!incomingCallData.latestIncomingCall,
  });

  const { data: image } = useGetPersonImageQuery(callInfo.contact?.PersonID ?? '', {
    enabled: !!incomingCallData.latestIncomingCall && !!callInfo.contact && !mergedCallGroup,
  });

  const toggleActiveCallBar = () => {
    if (isMobile) return open();
    if (mode === 'modal') return goToDefaultRoute();
    if (isOpen) close();
    else open();
  };

  const callData = useMemo(() => {
    return {
      ...incomingCallData,
      ...callInfo,
      image,
    };
  }, [incomingCallData, callInfo, image]);

  return isMobile ? (
    <MobileCallBars
      numOfIncomingCalls={incomingCalls?.length}
      callData={callData}
      toggleActiveCallBar={toggleActiveCallBar}
      hasActiveCalls={hasActiveCalls}
      showIncomingCallsUI={showIncomingCallsUI}
    />
  ) : (
    <DesktopCallBars
      toggleActiveCallBar={toggleActiveCallBar}
      isOpen={isOpen}
      hasActiveCalls={hasActiveCalls}
      numOfIncomingCalls={incomingCalls?.length}
      callData={callData}
      showIncomingCallsUI={showIncomingCallsUI}
    />
  );
};
